import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import ThemeToggle from "@/components/ThemeToggle";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useTheme } from "next-themes";
import {
  MessageSquare, Plus, Trash2, Send, Settings, LogOut, User,
  ChevronLeft, ChevronRight, Bot, Copy, Check, Menu, Shield, Sparkles,
  Paperclip, X, FileText, CreditCard, Loader2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── CODE BLOCK ────────────────────────────────────────────────────────────
function CodeBlock({ language, value }) {
  const { resolvedTheme } = useTheme();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative my-3 rounded-lg overflow-hidden border border-border">
      <div className="flex items-center justify-between px-4 py-1.5 bg-muted/80 border-b border-border">
        <span className="text-xs text-muted-foreground font-mono">{language || "code"}</span>
        <button onClick={copy} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded">
          {copied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || "text"}
        style={resolvedTheme === "dark" ? oneDark : oneLight}
        customStyle={{ margin: 0, borderRadius: 0, fontSize: "0.85rem", background: "transparent" }}
        PreTag="div"
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
}

// ─── MESSAGE BUBBLE ────────────────────────────────────────────────────────
function MessageBubble({ role, content, isStreaming }) {
  if (role === "user") {
    return (
      <div className="flex justify-end message-in">
        <div className="max-w-[80%] bg-primary/10 dark:bg-primary/15 border border-primary/20 rounded-2xl rounded-tr-sm px-4 py-3">
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-3 message-in">
      <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 mt-1 shadow-sm">
        <Bot className="h-4 w-4 text-primary-foreground" />
      </div>
      <div className="flex-1 min-w-0 max-w-[90%]">
        <div className={`chat-markdown text-sm ${isStreaming ? "streaming-cursor" : ""}`}>
          {content ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || "");
                  if (!inline && match) {
                    return <CodeBlock language={match[1]} value={String(children).replace(/\n$/, "")} />;
                  }
                  return <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props}>{children}</code>;
                },
                pre({ children }) { return <>{children}</>; },
              }}
            >
              {content}
            </ReactMarkdown>
          ) : (
            isStreaming && <span className="text-muted-foreground text-sm">Přemýšlím...</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── WELCOME SCREEN ────────────────────────────────────────────────────────
function WelcomeScreen({ userName, onPrompt }) {
  const prompts = [
    "Vysvětli mi kvantové počítání jednoduše",
    "Napiš Python skript pro řazení souborů podle data",
    "Pomoz mi napsat profesionální e-mail pro změnu termínu schůzky",
    "Jaké jsou osvědčené postupy pro optimalizaci výkonu v Reactu?",
  ];
  return (
    <div className="flex flex-col items-center justify-center h-full px-4 py-12 text-center fade-in">
      <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-6 shadow-lg">
        <Sparkles className="h-8 w-8 text-primary-foreground" />
      </div>
      <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
        Ahoj, {userName?.split(" ")[0] || "uživateli"}!
      </h2>
      <p className="text-muted-foreground mb-8 text-sm max-w-sm">
        Jak vám dnes mohu pomoci? Vyberte návrh nebo napište vlastní zprávu.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl w-full">
        {prompts.map((p) => (
          <button
            key={p}
            onClick={() => onPrompt(p)}
            className="text-left px-4 py-3 rounded-xl border border-border bg-card hover:bg-muted hover:border-primary/30 transition-all text-sm text-foreground/80 hover:text-foreground"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN CHAT PAGE ────────────────────────────────────────────────────────
export default function ChatPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState([]);
  const [currentConvId, setCurrentConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedModel, setSelectedModel] = useState("gpt-4o");
  const [selectedProvider, setSelectedProvider] = useState("openai");
  const [availableModels, setAvailableModels] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]); // [{name, text, chars}]
  const [uploadingFile, setUploadingFile] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/chat/conversations`, { credentials: "include" });
      if (res.ok) setConversations(await res.json());
    } catch {}
  }, []);

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetch(`${API}/models`, { credentials: "include" });
      if (res.ok) setAvailableModels(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchModels();
  }, [fetchConversations, fetchModels]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectConversation = async (convId) => {
    if (convId === currentConvId) return;
    try {
      const res = await fetch(`${API}/chat/conversations/${convId}`, { credentials: "include" });
      if (res.ok) {
        const conv = await res.json();
        setMessages(conv.messages || []);
        setCurrentConvId(convId);
        setSelectedModel(conv.model || "gpt-4o");
        setSelectedProvider(conv.provider || "openai");
      }
    } catch {}
  };

  const newChat = () => {
    setCurrentConvId(null);
    setMessages([]);
    textareaRef.current?.focus();
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    await fetch(`${API}/chat/conversations/${convId}`, { method: "DELETE", credentials: "include" });
    if (currentConvId === convId) newChat();
    setConversations((prev) => prev.filter((c) => c.conversation_id !== convId));
    toast.success("Conversation deleted");
  };

  const sendMessage = async () => {
    if (!inputValue.trim() && attachedFiles.length === 0) return;
    if (isStreaming) return;

    // Sestavit obsah zprávy s přílohami
    let content = inputValue.trim();
    if (attachedFiles.length > 0) {
      const fileContext = attachedFiles.map(f =>
        `\n\n--- Příloha: ${f.filename} ---\n${f.text}`
      ).join("\n");
      content = content ? `${content}${fileContext}` : `Analyzuj přiložené soubory:${fileContext}`;
    }

    setInputValue("");
    setAttachedFiles([]);
    setIsStreaming(true);

    const userMsgId = `u-${Date.now()}`;
    const assistantMsgId = `a-${Date.now()}`;
    setStreamingMsgId(assistantMsgId);

    setMessages((prev) => [
      ...prev,
      { id: userMsgId, role: "user", content, timestamp: new Date().toISOString() },
      { id: assistantMsgId, role: "assistant", content: "", isStreaming: true },
    ]);

    let convId = currentConvId;

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          conversation_id: convId || undefined,
          content,
          model: selectedModel,
          provider: selectedProvider,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to send message");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          try {
            const parsed = JSON.parse(raw);
            if (parsed.conversation_id && !convId) {
              convId = parsed.conversation_id;
              setCurrentConvId(convId);
            }
            if (parsed.content) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: m.content + parsed.content } : m
                )
              );
            }
            if (parsed.done) {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantMsgId ? { ...m, isStreaming: false } : m))
              );
              fetchConversations();
            }
            if (parsed.error) throw new Error(parsed.error);
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.startsWith("Unexpected")) {
              throw parseErr;
            }
          }
        }
      }
    } catch (err) {
      toast.error(err.message || "Failed to send message");
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, content: `Error: ${err.message}`, isStreaming: false }
            : m
        )
      );
    } finally {
      setIsStreaming(false);
      setStreamingMsgId(null);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/chat/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || "Nelze nahrát soubor");
        return;
      }
      const data = await res.json();
      setAttachedFiles((prev) => [...prev, data]);
      toast.success(`Soubor "${data.filename}" nahrán (${data.chars} znaků)`);
    } catch {
      toast.error("Chyba při nahrávání souboru");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = (idx) => setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleModelChange = (val) => {
    const [provider, ...rest] = val.split(":");
    const model = rest.join(":");
    setSelectedProvider(provider);
    setSelectedModel(model);
  };

  const openaiModels = availableModels.filter((m) => m.provider === "openai");
  const anthropicModels = availableModels.filter((m) => m.provider === "anthropic");
  const geminiModels = availableModels.filter((m) => m.provider === "gemini");
  const ollamaModels = availableModels.filter((m) => m.provider === "ollama");

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <TooltipProvider>
      <div className="flex h-screen overflow-hidden bg-background">
        {/* ── SIDEBAR ─────────────────────────────────────────────── */}
        <aside
          data-testid="chat-sidebar"
          className={`${sidebarOpen ? "w-64" : "w-0"} flex-shrink-0 overflow-hidden transition-all duration-200 border-r border-border flex flex-col`}
          style={{ background: "hsl(var(--sidebar-bg))" }}
        >
          {/* Logo */}
          <div className="flex items-center gap-2.5 px-4 py-4 border-b border-border flex-shrink-0">
            <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <span className="font-semibold text-sm truncate" style={{ fontFamily: "Outfit, sans-serif" }}>ChatGreen</span>
          </div>

          {/* New Chat */}
          <div className="p-2 flex-shrink-0">
            <Button
              variant="outline"
              className="w-full gap-2 h-9 text-sm justify-start"
              onClick={newChat}
              data-testid="new-chat-button"
            >
              <Plus className="h-3.5 w-3.5" />
              New Chat
            </Button>
          </div>

          {/* Conversations list */}
          <div className="flex-1 overflow-y-auto px-2 space-y-0.5 min-h-0">
            {conversations.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6 px-4">Zatím žádné konverzace. Začněte psát!</p>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  onClick={() => selectConversation(conv.conversation_id)}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
                    currentConvId === conv.conversation_id
                      ? "bg-primary/10 text-foreground border border-primary/20"
                      : "hover:bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`conversation-item-${conv.conversation_id}`}
                >
                  <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
                  <span className="flex-1 truncate text-xs">{conv.title || "New Chat"}</span>
                  <button
                    onClick={(e) => deleteConversation(conv.conversation_id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:text-destructive transition-all rounded"
                    data-testid={`delete-conv-${conv.conversation_id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* User area */}
          <div className="p-2 border-t border-border flex-shrink-0">
            {user?.role === "admin" && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 h-8 text-xs text-muted-foreground hover:text-foreground mb-1"
                onClick={() => navigate("/admin")}
                data-testid="admin-panel-link"
              >
                <Shield className="h-3.5 w-3.5" />
                Admin panel
              </Button>
            )}
            {user?.credits !== -1 && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 h-8 text-xs text-primary hover:text-primary/80 mb-1"
                onClick={() => navigate("/billing")}
                data-testid="billing-button"
              >
                <CreditCard className="h-3.5 w-3.5" />
                Dobít kredity
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted transition-colors" data-testid="user-menu-button">
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {user?.picture ? (
                      <img src={user.picture} alt="avatar" className="w-full h-full object-cover" />
                    ) : (
                      <User className="h-3.5 w-3.5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-xs font-medium truncate">{user?.name || "User"}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.credits === -1 ? "Unlimited" : `${user?.credits ?? 0} credits`}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="h-4 w-4 mr-2" />
                  Odhlásit se
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* ── MAIN ──────────────────────────────────────────────────── */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Header */}
          <header className="flex items-center gap-3 px-4 py-3 border-b border-border flex-shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  data-testid="sidebar-toggle-button"
                >
                  {sidebarOpen ? <ChevronLeft className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{sidebarOpen ? "Close sidebar" : "Open sidebar"}</TooltipContent>
            </Tooltip>

            <div className="flex items-center gap-2 flex-1">
              <Select
                value={`${selectedProvider}:${selectedModel}`}
                onValueChange={handleModelChange}
                data-testid="model-selector"
              >
                <SelectTrigger className="w-[180px] h-8 text-xs border-border" data-testid="model-selector-trigger">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {openaiModels.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium">OpenAI</div>
                      {openaiModels.map((m) => (
                        <SelectItem key={m.id} value={`openai:${m.id}`} className="text-xs">{m.name}</SelectItem>
                      ))}
                    </>
                  )}
                  {anthropicModels.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium mt-1">Anthropic Claude</div>
                      {anthropicModels.map((m) => (
                        <SelectItem key={m.id} value={`anthropic:${m.id}`} className="text-xs">{m.name}</SelectItem>
                      ))}
                    </>
                  )}
                  {geminiModels.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium mt-1">Google Gemini</div>
                      {geminiModels.map((m) => (
                        <SelectItem key={m.id} value={`gemini:${m.id}`} className="text-xs">{m.name}</SelectItem>
                      ))}
                    </>
                  )}
                  {ollamaModels.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs text-muted-foreground font-medium mt-1">Ollama (Místní)</div>
                      {ollamaModels.map((m) => (
                        <SelectItem key={m.id} value={`ollama:${m.id}`} className="text-xs">{m.name}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              {selectedProvider === "ollama" && (
                <Badge variant="outline" className="text-xs border-primary/40 text-primary">Local</Badge>
              )}
              {selectedProvider === "anthropic" && (
                <Badge variant="outline" className="text-xs border-orange-500/40 text-orange-500">Claude</Badge>
              )}
              {selectedProvider === "gemini" && (
                <Badge variant="outline" className="text-xs border-blue-500/40 text-blue-500">Gemini</Badge>
              )}
            </div>

            <ThemeToggle />
          </header>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto min-h-0" data-testid="messages-container">
            <div className="max-w-4xl mx-auto px-4 py-6">
              {messages.length === 0 ? (
                <WelcomeScreen userName={user?.name} onPrompt={(p) => { setInputValue(p); textareaRef.current?.focus(); }} />
              ) : (
                <div className="space-y-6">
                  {messages.map((msg, idx) => (
                    <MessageBubble key={msg.id || idx} role={msg.role} content={msg.content} isStreaming={msg.isStreaming} />
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <div className="px-4 pb-4 pt-2 flex-shrink-0 border-t border-border">
            <div className="max-w-4xl mx-auto">
              {/* Attached files preview */}
              {attachedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {attachedFiles.map((f, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-primary/10 border border-primary/20 rounded-lg px-3 py-1.5 text-xs">
                      <FileText className="h-3 w-3 text-primary flex-shrink-0" />
                      <span className="truncate max-w-[150px]">{f.filename}</span>
                      <span className="text-muted-foreground">({f.chars} znaků)</span>
                      <button onClick={() => removeFile(idx)} className="ml-1 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-end bg-muted/50 rounded-xl border border-border p-3 focus-within:border-primary/50 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.txt,.md,.csv,.py,.js,.ts,.html,.css,.json,.xml"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile || isStreaming}
                  className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0 mb-1"
                  title="Přiložit soubor (PDF, TXT, ...)"
                  data-testid="attach-file-button"
                >
                  {uploadingFile ? (
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                </button>
                <Textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={attachedFiles.length > 0 ? "Položte otázku k přiloženým souborům..." : "Napište zprávu... (Enter odeslat, Shift+Enter nový řádek)"}
                  className="flex-1 min-h-[44px] max-h-[200px] resize-none border-0 bg-transparent p-0 focus-visible:ring-0 text-sm leading-relaxed"
                  rows={1}
                  disabled={isStreaming}
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={(!inputValue.trim() && attachedFiles.length === 0) || isStreaming}
                  size="icon"
                  className="h-9 w-9 rounded-lg bg-primary hover:bg-primary/90 flex-shrink-0"
                  data-testid="chat-send-button"
                >
                  {isStreaming ? (
                    <div className="w-3.5 h-3.5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5 text-primary-foreground" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                ChatGreen může dělat chyby. Důležité informace si ověřte.
              </p>
            </div>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
